// ══════════════════════════════════════════════════
// MARK: Setup
// ──────────────────────────────────────────────────
// #═#═#═#═#═# Imports #═#═#═#═#═#
import { header, root, adminbar } from '../../module/element.js';
import { is } from '../../module/type.js';
import {
	Events,
	ScrollAbortEvent,
	ScrollDirectionChangedEvent,
	ScrollEvent,
	ScrollStartEvent,
	ScrollToEvent
} from './events.js';
import { debounce } from '../../module/timing.js';
// @ts-ignore
const	$	=	jQuery;


// ══════════════════════════════════════════════════
// MARK: Types
// ──────────────────────────────────────────────────
// #═#═#═#═#═# Declaration #═#═#═#═#═#
declare global {
	interface Number {
		/**
		 * Checks if the number is within the given range
		 * @param		min		The minimum value
		 * @param		max		The maximum value
		 * @returns		True if the number is within the range, false otherwise
		 */
		inRange(min: number, max: number): boolean;
		/**
		 * Clamps the number to the given range
		 * @param		min		The minimum value
		 * @param		max		The maximum value
		 * @returns		The clamped number
		 */
		clamp(min: number, max: number): number;
	}
}


// #═#═#═#═#═# Namespace #═#═#═#═#═#
export declare namespace Scroll {
	/** A coordinate-like object with x and y properties */
	export interface CoordinateLike<Tx = number, Ty = Tx> {
		x: Tx;
		y: Ty;
	}

	/** Relative directions for scroll events */
	export type Directions = CoordinateLike<'left' | 'right' | undefined, 'up' | 'down' | undefined>;

	/** An object with x and y coordinates as numbers */
	export type Coordinates = CoordinateLike<number>;

	/** Options for the scroll animation */
	export type Options = {
		/** The duration for the animation */
		duration?: number;
		/** The easing function used to animate */
		easing?: 'swing' | 'linear' | 'easein' | 'easeout' | 'easeinout' | 'easeInBounce' | 'easeOutBounce' | 'easeInOutBounce';
		/** If true, the scroll will be added to a queue instead of being executed immediately */
		queue?: boolean;
		/** The additional offset added to the fetched element coordinates */
		offset?: {
			x?: number;
			y?: number;
			/** If true the height of the header element will be added to the offset */
			header?: boolean;
			/** If true the height of the WP-admin bar will be added to the offset */
			adminbar?: boolean;
		};
		/** The element to scroll to */
		element?: HTMLElement;
		/** If true, the scroll will not be animated */
		instant?: boolean;
	};

	/**
	 * An advanced target for the scrollTo method
	 * - Can either be an object with x and y coordinates as numbers
	 * - or an object with additional options (except x and y coordinates)
	 */
	export type AdvancedTarget = Partial<Coordinates & Options>;

	/**
	 * A valid target for the scrollTo method
	 * - `number`: interpreted as the y-coordinate to scroll to
	 * - `string`: interpreted as a CSS-Selector to find the element to scroll to
	 * - `HTMLElement`: the element to scroll to
	 * - `AdvancedTarget`: an object with x and y coordinates or an element and additional options
	 */
	export type Target	=	number | string | HTMLElement | AdvancedTarget;

	/** Keys of all registered events */
	export type EventNames = keyof typeof Scroll["events"];

	/** The type of a registered event */
	export type RegisteredEvents<K extends EventNames = EventNames> = K extends keyof typeof Scroll["events"] 
		? typeof Scroll["events"][K]
		: never;
}


// ══════════════════════════════════════════════════
// MARK: Scroll Controller
// ──────────────────────────────────────────────────
/**
 * This class handles all Scroll related tasks
 * @since		6.0.0.6
 * @version		1.2
 * @author		Elko Keil
 * @package		scroll
 */
export class Scroll {
	// #═#═#═#═#═# Properties #═#═#═#═#═#
		// #════ Initialised	| Static ════#
	/**	Debug Boolean */
	public static	debug:boolean				= false;
	/**	The minimum Scroll position */
	public static	pageTop:number				= 0;
	/** Subtract the height of the header */
	public static	respectHeaderByDefault:boolean		= true;
	/** Subtract the height of the adminbar */
	public static	respectAdminbarByDefault:boolean	= true;
	/** Append Hash to History */
	public static	appendHashToHistory:boolean	= true;
	/** Last Header Height */
	private static lastHeaderHeight: number = 0;
	/** The Threshold in pixels to evaluate a header size change */
	private static headerHeightChangeThreshold: number = 2;
	/** Last scroll directions */
	public static	lastScrollDirection: Scroll.Directions		= Object.seal({
		x: undefined,
		y: undefined,
	});
	/**	The current Scroll position */
	private static readonly	currentScroll:Scroll.Coordinates	= Object.seal({
		x: root.scrollLeft,
		y: root.scrollTop,
	});
	/**	The maximum Scroll position */
	private static	minScrollY:number				= 0;
	/**	The maximum Scroll position */
	private static	maxScrollY:number				= root.scrollHeight - window.innerHeight;
	/** Lock the horizontal Scroll */
	public static	lockHorizontalScroll:boolean	= true;
	/**	Base duration of an animation in ms */
	private static	baseDuration:number				= 300;
	/**	Speed Index of the Animation */
	private static	speed:number					= 1;
	/**	Screen Height */
	private static readonly	screenHeight:number		= window.innerHeight;


		// #════ Events ════#
	/**	Events that can be listened to */
	public static readonly	events = Object.freeze(Events);

	/**
	 * Adds an event listener to the scroll direction changed event
	 * @param listener The listener to add
	 */
	private static readonly listeners: WeakMap<Function, Set<((event: ScrollEvent) => void)>> = new WeakMap([
		[ScrollDirectionChangedEvent.constructor,	new Set<((event: ScrollDirectionChangedEvent) => void)>()],
		[ScrollStartEvent.constructor,				new Set<((event: ScrollStartEvent) => void)>()],
		[ScrollStartEvent.constructor,				new Set<((event: ScrollStartEvent) => void)>()],
		[ScrollAbortEvent.constructor,				new Set<((event: ScrollAbortEvent) => void)>()],
		[ScrollToEvent.constructor,					new Set<((event: ScrollToEvent) => void)>()],
	]);


	// #═#═#═#═#═# Constructor #═#═#═#═#═#
	private constructor() {
		// NON CONSTROCTING
	}


	//
	// MARK: Events
	//
	// #═#═#═#═#═# Add Event Listener #═#═#═#═#═#
	/**
	 * Adds a listener to the events
	 * @param type The type of the event to listen to
	 * @param listener The listener to add
	 */
	public static addEventListener<K extends Scroll.EventNames>(
		type: K,
		listener: (event: ScrollEvent) => void
	): void {
		// #════ Get Listeners ════#
		const constructor = Events.Type(type).constructor;
		const listeners	= Scroll.listeners.get(constructor);


		// #════ Guard ════#
		if(!listeners) {
			throw new Error(`Scroll.addEventListener: The event "${type}" does not exist.`);
		}

		if (listeners.has(listener)) {
			if (this.debug) console.warn(`Scroll.addEventListener: Listener for event "${type}" is already registered.`);
			return;
		}


		// #════ Add Listener ════#
		listeners.add(listener);
	}


	// #═#═#═#═#═# Remove Event Listener #═#═#═#═#═#
	/**
	 * Removes a listener from events
	 * @param type The type of the event to remove the listener from
	 * @param listener The listener to remove
	 * 
	 * @returns True if the listener was removed, false otherwise
	 */
	public static removeEventListener<K extends Scroll.EventNames>(
		type: K,
		listener: (event: ScrollEvent) => void
	): boolean {
		// #════ Get Listeners ════#
		const constructor = Events.Type(type).constructor;
		const listeners	= Scroll.listeners.get(constructor);


		// #════ Guard ════#
		if(!listeners) {
			throw new Error(`Scroll.removeEventListener: The event "${type}" does not exist.`);
		}


		// #════ Remove Listener ════#
		return listeners.delete(listener);
	}


	// #═#═#═#═#═# Dispatch Event #═#═#═#═#═#
	/**
	 * Dispatches an event to all listeners
	 * @param event The type of the event to dispatch
	 */
	public static dispatchEvent<T extends Scroll.EventNames = Scroll.EventNames, K = Scroll.RegisteredEvents<T>>(
		event: K,
	): void {
		// #════ Guard ════#
		if(!event) {
			throw new Error(`Scroll.dispatchEvent: The event "${event?.type}" does not exist.`);
		}


		// #════ Get Entries ════#
		const type = event.type as T;
		const constructor = Events.Type(type).constructor;
		const listeners	= Scroll.listeners.get(constructor);


		// #════ Guard ════#
		if(!listeners || listeners.size === 0) {
			if(this.debug) console.warn(`Scroll.dispatchEvent: No listeners for the event "${event}" found.`);
			return;
		}


		// #════ Dispatch Event ════#
		this.#executeEvents(listeners, event as unknown as ScrollEvent);
	}


	// #═#═#═#═#═# Async Event Execution #═#═#═#═#═#
	/**
	 * Executes an event asynchronously
	 */
	static #executeEvents<E extends ScrollEvent>(
		listeners: Set<((event: E) => void)>,
		event: E
	): void {
		// #════ Guard ════#
		if(!listeners || listeners.size === 0) {
			if(this.debug) console.warn(`Scroll.#executeEvents: No listeners for the event "${event.type}" found.`);
			return;
		}


		for(const listener of listeners) {
			queueMicrotask(() => {
				try {
					listener(event);
				} catch(error) {
					if(this.debug) console.error(`Scroll.dispatchEvent: An error occurred while dispatching the event "${event.type}".`, error);
				}
			});
		}


		// #════ Log ════#
		if(this.debug) console.log(`Scroll.dispatchEvent: Dispatched event "${event.type}" to ${listeners.size} listeners.`, event);
	}


	//
	// MARK: Methods
	//
	// #═#═#═#═#═# Get Distance #═#═#═#═#═#
	/**
	 * Retrieves the relative distance between the current scroll position and the given coordinates
	 * @param		x		X destination
	 * @param		y		Y destination
	 * @returns		Distance to the given coordinates
	 */
	private static getDistance(
		x:number,
		y:number
	): Scroll.Coordinates {
		return {
			x: Math.abs(x - root.scrollLeft),
			y: Math.abs(y - root.scrollTop)
		}
	}


	// #═#═#═#═#═# Get Duration #═#═#═#═#═#
	/**
	 * Retrieves the duration of the scroll animation based on the given position difference to the target
	 * @param		delta		The distance to the target
	 * @returns		The duration of the scroll animation in ms
	 */
	private static getDuration(
		delta: Scroll.Coordinates
	): number {
		// #════ Calculate Delta ════#
		const duration	= this.baseDuration * delta.y / this.screenHeight;


		// #════ Set Minimum ════#
		if(duration < this.baseDuration) return this.baseDuration;


		// #════ Return ════#
		return duration;
	}


	// #═#═#═#═#═# Get Element Coordinates #═#═#═#═#═#
	/**
	 * Retrieves the coordinates of an Element relative to the page
	 * @param		element		The Element to retrieve the coordinates from
	 * @returns		The coordinates of the Element
	 */
	private static getElementCoordinates(
		element:HTMLElement
	): Scroll.Coordinates {
		// #════ Guard ════#
		if(!is.element(element)) {
			throw new Error('Scroll.to: The target needs to be a Number, HTMLElement or Object.');
		}


		// #════ Get Coordinates ════#
		const {left, top}	= element.getBoundingClientRect();
		return {
			x: left + window.scrollX,
			y: top + window.scrollY
		};
	}


	//
	// MARK: Scrolling
	//
	// #═#═#═#═#═# To #═#═#═#═#═#
	/**
	 * Scrolls to given coordinates or Element
	 * @param		target		The target to scroll to
	 * @param		x			The x coordinate to scroll to
	 */
	public static to(y: number, x: number): void;	// Coordinates
	public static to(target:string): void;			// Selector
	public static to(target:Scroll.Target): void;	// Target Object
	public static to(
		target:Scroll.Target,
		x?:number
	): void {
		// #════ Identefy Type ════#
		switch(true) {
			case is.number(target):
				// +──── Convert Type ────+
				target	=	{y: target as number};

				// +──── Set Y ────+
				if(!!x) {
					target.x	=	x;
				}
				break;

			case is.string(target):
				// +──── Convert Type ────+
				const element	=	document.querySelector(target as string);
				if(!element) {
					throw new Error(`Scroll.to: The target "${target}" could not be found.`);
				}
				target = element as HTMLElement;
				// No-break;

			case is.element(target):
				// +──── Convert Type ────+
				target		=	Object.assign({
					element: target as HTMLElement,
				}, Scroll.getElementCoordinates(target as HTMLElement));
				break;

			case is.object(target):
				// +──── Guard ────+
				target = target as Partial<Scroll.Coordinates &Scroll.Options>;
				if(target.x == undefined && target.y == undefined && !is.element(target.element)) {
					throw new Error('Scroll.to: The target object needs to have at least one coordinate.');
				}


				// +──── Convert Type ────+
				if(target.element && is.element(target.element)) {
					const coordinates	=	Scroll.getElementCoordinates(target.element);
					target				=	Object.assign(target, coordinates);
				}
				break;

			default:
				// +---- Fail ----+
				throw new Error('Scroll.to: The target needs to be a Number, HTMLElement or Object.');
		}

		// Redefine Type
		target = target as Scroll.Coordinates & Partial<Scroll.Options>;


		// #════ Animation Options ════#
		// Set up the default animation options
		const options:Scroll.Options	= {
			duration: 0,
			easing: 'swing',
			queue: false,
		};


		// #════ Calculate Delta ════#
		// If smooth scrolling is enabled, calculate a smoother scroll duration
		if(!Boolean(target.instant)) {
			const delta			= this.getDistance(target.x as number, target.y as number);
			target.duration		??= this.getDuration(delta);

			options.duration	= target.duration / this.speed;
		}


		// #════ Target Destination ════#
		// Animate the windows scroll position
		const destination: Record<string, number>	=	{};
		if(!this.lockHorizontalScroll && target.x !== undefined) destination.scrollLeft	=	target.x;
		if(target.y !== undefined) destination.scrollTop	=	target.y;


		// #════ Offsets ════#
			// +──── Elements ────+
		if(header && (target.offset?.header ?? this.respectHeaderByDefault)) {
			const headerHeight = this.heightOf(header);
			destination.scrollTop	-=	headerHeight;
		}

		if(adminbar && (target.offset?.adminbar ?? this.respectAdminbarByDefault)) {
			const adminbarHeight = this.heightOf(adminbar);
			destination.scrollTop	-=	adminbarHeight;
		}

		// Make sure the scroll position is not above the top of the page
		destination.scrollTop = Math.max(destination.scrollTop, this.pageTop);


			// +──── Calculations ────+
		if(target.x && target.offset?.x) destination.scrollLeft	+=	target.offset?.x;
		if(target.y && target.offset?.y) destination.scrollTop	+=	target.offset?.y;


		// #════ Validate Range ════#
		// Adjust the scroll position to the minimum and maximum scroll position
		if(Boolean(destination.scrollLeft) && destination.scrollLeft < 0) destination.scrollLeft = 0;
		if(Boolean(destination.scrollTop) && !destination.scrollTop.inRange(this.minScrollY, this.maxScrollY)) {
			destination.scrollTop		=	destination.scrollTop.clamp(this.minScrollY, this.maxScrollY);
		}


		// #════ Before Scrolling ════#
		// Append the abort scrolling event listeners to prevent scrolling while the animation is running
		document.addEventListener('wheel', Scroll.abortScrollAnimation, {once: true});
		document.addEventListener('touchstart', Scroll.abortScrollAnimation, {once: true});


		// #════ Dispatch Scroll Start ════#
		// Dispatch the scroll start event
		Scroll.dispatchEvent(new this.events.ScrollTo({
			target: target,
			destination: destination,
			options: options
		}));


		// Append Hash to location History
		if(this.appendHashToHistory && is.element(target.element)) {
			const element = target.element as HTMLElement;

			this._doAppendHashHistory(element);
		}


		// #════ Run Animation ════#
		const eventDetails = {
			target: target,
			speed: this.speed,
			duration: options.duration,
		};
		Scroll.dispatchEvent(new this.events.ScrollStart(eventDetails));
		$(root).stop().animate(destination, options);


		// #════ After Animation Complete ════#
		$(root).promise().done(() => {
			// +──── Remove EventListener ────+
			document.removeEventListener('wheel', Scroll.abortScrollAnimation);
			document.removeEventListener('touchstart', Scroll.abortScrollAnimation);

			// +──── Dispatch Scroll End ────+
			Scroll.dispatchEvent(new this.events.ScrollEnd(eventDetails));
		});
	}


	// #═#═#═#═#═# To Top #═#═#═#═#═#
	/**
	 * Scrolls to the top of the page
	 */
	public static toTop() {
		Scroll.to({
			y: this.pageTop,
			instant: false
		});
	}


	// #═#═#═#═#═# Abort Scrolling #═#═#═#═#═#
	/**
	 * Aborts the current scrolling animation when the the scrollwheel is used or a touch event is triggered
	 * @param		event		The event that triggered the abort
	 * @returns		True if the scrolling was aborted, false otherwise
	 */
	public static abortScrollAnimation = (
	): boolean => {
		// #════ Check if Scrolling is Active ════#
		if(!$(root).is(':animated')) {
			if(this.debug) console.warn('Scroll.abortScrolling: No scrolling animation is currently active.');
			return false;
		}


		// #════ Abort Scrolling ════#
		// Stop the current scrolling animation
		try {
			$(root).stop(true, false);
			// Dispatch the scroll abort event
			Scroll.dispatchEvent(new this.events.ScrollAbort({
				target: {
					x: root.scrollLeft,
					y: root.scrollTop
				},
				speed: this.speed,
			}));
			return true;
		} catch(error) {
			if(this.debug) console.error('Scroll.abortScrolling: An error occurred while stopping the scrolling animation.', error);
			return false;
		}
	}


	//
	// MARK: Window History
	//
	// #═#═#═#═#═# Append Anchor Hash to history #═#═#═#═#═#
	/**
	 * Appends the anchor hash to the history
	 * @param		target		The anchor element to append the hash from
	 * @returns		{void}
	 */
	private static _doAppendHashHistory(
		target: string|HTMLElement
	): void {
		// #════ Validate ════#
		if(typeof target === 'string') {
			if(target.startsWith('#')) {
				// If the anchor is a string, remove the hash
				target	=	target.substring(1);
			}

			// If the anchor is a string, try to get the element
			const maybeTarget	=	document.getElementById(target);
			if(!maybeTarget) {
				if(this.debug) console.warn(`Scroll.to: The anchor "#${target}" could not be found.`);
				return;
			}

			target	=	maybeTarget;
		}


		// #════ Guard ════#
		if(!target.id) {
			return;
		}
		
		if(history?.pushState) {
			history.pushState(null, '', `#${target.id}`);
		} else {
			window.location.hash	= target.id;
		}
	}


	//
	// MARK: Utilities
	//
	// #═#═#═#═#═# Is Same Site Anchor #═#═#═#═#═#
	/**
	 * Determines if the given anchor is only to scroll within the same page
	 * @param anchor The anchor element to check
	 * @returns 
	 */
	private static _isSameSiteAnchor(
		anchor: HTMLAnchorElement|null
	): boolean {
		// #════ Guard ════#
		if(!anchor) {
			if(this.debug) console.warn('Scroll.to: The anchor element is null or undefined.');
			return false;
		}


		// #════ Variables ════#
		// Get the current location info
		const { pathname, hostname }	= window.location;


		// #════ Check Cases ════#
		if(!anchor.hash) {
			// If the anchor has no hash, cant be anchor link
			return false;
		}

		
		// Host or Path does not match
		if(
			(!is.empty(anchor.hostname) && anchor.hostname !== hostname) ||
			(!is.empty(anchor.pathname) && anchor.pathname !== pathname)
		) {
			return false;
		}

		// If the anchor has a hash and stays within the same page, it is a same site anchor
		return true;
	}

	/**
	 * Gets the current height of the header element
	 */
	private static heightOf(
		element: HTMLElement
	): number {
		if(!is.element(element)) {
			return 0;
		}

		return element.getBoundingClientRect().height;
	}


	/**
	 * Gets the current height of the header element
	 */
	private static hasHeaderHeightChanged(): boolean {
		const currentHeight = this.heightOf(header);
		const hasChanged = Math.abs(currentHeight - this.lastHeaderHeight) > this.headerHeightChangeThreshold;
		
		if (hasChanged) {
			this.lastHeaderHeight = currentHeight;
		}
		
		return hasChanged;
	}


	//
	// MARK: Actions
	//
	// #═#═#═#═#═# on Pointer Interaction #═#═#═#═#═#
	static #onPointerInteraction = (event: MouseEvent) => {
		// #════ Guard ════#
		let anchor	= (event.target as HTMLElement).closest('a');
		if(!anchor) return;


		// #════ Check for Hash ════#
		if(this._isSameSiteAnchor(anchor)) {
			// Prevent Default
			event.preventDefault();

			// Get Target
			const target	=	document.querySelector(anchor.hash) as HTMLElement|null;

			if(!target) {
				if(this.debug) console.warn(`Scroll.to: The target "${anchor.hash}" could not be found.`);
				return;
			}

			// Scroll to Target
			this.to(target);
		}
	};


	// #═#═#═#═#═# on Hash Change #═#═#═#═#═#
	static #onHashChange = (event: HashChangeEvent) => {
		// #════ Guard ════#
		if(!event.newURL) return;


		// #════ Get Hash ════#
		const hash = event.newURL.split('#')[1];
		if(!hash) {
			return;
		}


		// #════ Get Target ════#
		const target	=	document.getElementById(hash);
		if(!target) {
			if(this.debug) console.warn(`Scroll.to: The target "${hash}" could not be found.`);
			return;
		}


		// #════ Scroll to Target ════#
		try {
			this.to({
				element: target,
				instant: true,
			});
		} catch(error) {
			if(this.debug) console.error(`Scroll.to: An error occurred while scrolling to the target "${hash}".`, error);
		}
	}


	// #═#═#═#═#═# Direction Check #═#═#═#═#═#
	/**
	 * Checks the current scroll direction and dispatches an event if it has changed
	 */
	private static _checkScrollDirection = (): void => {
		// #════ Get Current Scroll ════#
		const currentScrollY	= root.scrollTop;
		const currentScrollX	= root.scrollLeft;


		// #════ Guard ════#
		// Check if header height changed (which might cause scroll events)
		if (this.hasHeaderHeightChanged()) {
			if (this.debug) console.log('Scroll._checkScrollDirection: Header height changed, skipping direction check');
			
			// Update current scroll to prevent false direction change detection
			this.currentScroll.y	= currentScrollY;
			this.currentScroll.x	= currentScrollX;
			return;
		}


		// #════ Get New Direction ════#
		let newDirectionY: 'up' | 'down' | undefined;
		let newDirectionX: 'left' | 'right' | undefined;

		switch(true) {
			case currentScrollY > this.currentScroll.y:
				newDirectionY = 'down';
				break;

			case currentScrollY < this.currentScroll.y:
				newDirectionY = 'up';
				break;
		}

		switch(true) {
			case currentScrollX > this.currentScroll.x:
				newDirectionX = 'right';
				break;

			case currentScrollX < this.currentScroll.x:
				newDirectionX = 'left';
				break;
		}


		// #════ Check Direction ════#
		if(newDirectionY && newDirectionY !== this.lastScrollDirection.y) {
			// Scrolling Vertically
			this.lastScrollDirection.y	= newDirectionY;

			// Log the direction
			if(this.debug) console.log(`Scroll._checkScrollDirection: Scrolling ${newDirectionY}`);
	
			// Dispatch the event
			this.dispatchEvent(new this.events.ScrollDirectionChanged({
				direction: newDirectionY,
			}));
		}

		if(newDirectionX && newDirectionX !== this.lastScrollDirection.x) {
			// Scrolling Horizontally
			this.lastScrollDirection.x	= newDirectionX;

			// Log the direction
			if(this.debug) console.log(`Scroll._checkScrollDirection: Scrolling ${newDirectionX}`);

			// Dispatch the event
			this.dispatchEvent(new this.events.ScrollDirectionChanged({
				direction: newDirectionX,
			}));
		}


		// #════ Update Current Scroll ════#
		this.currentScroll.y	= currentScrollY;
		this.currentScroll.x	= currentScrollX;
	};

	static #debouncedScrollDirection = debounce(this._checkScrollDirection, 50);


	//
	// MARK: Initialisation
	//
	// #═#═#═#═#═# Init #═#═#═#═#═#
	/**
	 * Initialises static values and events within the class
	 */
	public static __init(
	): void {
		// #════ On load ════#
		// Set Header height
		this.lastHeaderHeight = this.heightOf(header);

		// Scroll to the hash target when the page loads
		if(window.location.hash) {
			const target	=	document.querySelector(window.location.hash);

			if(target) {
				try {
					this.to({
						element: target as HTMLElement,
						instant: true,
					});
				} catch(error) {
					if(this.debug) console.error(`Scroll.to: An error occurred while scrolling to the target "${window.location.hash}".`, error);
				}
			}
		}


		// #════ Button Click ════#
		document.addEventListener('click', this.#onPointerInteraction);


		// #════ Hash Change ════#
		window.addEventListener('hashchange', this.#onHashChange);


		// #════ Scroll Direction ════#
		window.addEventListener('scroll', this.#debouncedScrollDirection, { passive: true });
	}
}
